/**
 * assessImageSuitability – evaluates whether an image has sufficient resolution
 * for its intended usage.
 *
 * Now context-aware via ImageIntent (central rule source).
 * Legacy ImageUsage ('cover' | 'gallery') is still accepted for backward compat.
 */

import { type ImageIntent, getIntentThresholds, usageToIntent } from './imageIntent';

export type ImageUsage = 'gallery' | 'cover';

export type ImageSuitability = 'ok' | 'warn' | 'block';

export interface SuitabilityResult {
  suitability: ImageSuitability;
  message?: string;
}

/**
 * Assess image suitability. Accepts either:
 * - Legacy `usage: ImageUsage` ('cover' | 'gallery') — mapped to intent internally
 * - New `intent: ImageIntent` via the overload
 */
export function assessImageSuitability(
  width: number,
  height: number,
  usageOrIntent: ImageUsage | ImageIntent,
): SuitabilityResult {
  // Map legacy usage to intent, or use intent directly
  const intent: ImageIntent =
    usageOrIntent === 'cover' || usageOrIntent === 'gallery'
      ? usageToIntent(usageOrIntent)
      : usageOrIntent;

  const thresholds = getIntentThresholds(intent);
  const longestSide = Math.max(width, height);

  console.debug('[assessImageSuitability]', { width, height, longestSide, intent });

  const dimLabel = `${width} × ${height} px`;
  const tinyHint = longestSide < 400
    ? ' Det verkar som att du valt en liten förhandsvisningsbild. Prova originalbilden istället.'
    : '';

  if (longestSide < thresholds.blockWidth) {
    return {
      suitability: 'block',
      message: `Bilden är ${dimLabel}. För ${thresholds.contextLabel} behöver längsta sidan vara minst ${thresholds.warnWidth} px.${tinyHint}`,
    };
  }

  if (longestSide < thresholds.goodWidth) {
    return {
      suitability: 'warn',
      message: `Bilden är ${dimLabel}. Den fungerar som ${thresholds.contextLabel} men högre upplösning (${thresholds.recommendedWidth}px) ger bättre resultat.`,
    };
  }

  return { suitability: 'ok' };
}

/**
 * Reads the natural dimensions of an image File by loading it into an Image element.
 * Returns { width, height }. Revokes the object URL after measurement.
 */
export function measureImageFile(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      URL.revokeObjectURL(url);
      resolve({ width: w, height: h });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read image dimensions'));
    };
    img.src = url;
  });
}
